import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useMatch, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Home,
  Plus,
  RefreshCw,
} from "lucide-react";
import SidebarNavIcon from "../ui/SidebarNavIcon";
import AccountProfileMenu, { getAccountInitial } from "../ui/AccountProfileMenu";
import { logoutAppUser, selectAppIsAuthenticated, selectAppUser } from "../../redux/appAuthSlice";
import {
  fetchHousePartners,
  fetchOperatorDashboard,
  fetchOperatorHouses,
} from "../../redux/operatorConsoleSlice";
import {
  formatCash,
  formatFounded,
  HOUSE_STATUS,
  houseMonogram,
  isPartneredHouse,
  getOperatorSection,
  OPERATOR_MANAGE_HOME,
  OPERATOR_NAV_CATEGORIES,
  operatorAddFixturesPath,
  operatorDashboardForHouse,
  operatorDeskPath,
  partnerCount,
  resolveOperatorHouse,
  shouldSyncDashboard,
} from "../../utils/operatorConsoleHelpers";
import { isBetHousePartnershipReadyForConsole } from "../../utils/betHousePartners";
import { OPERATOR_BRAND_ICON, OPERATOR_SECTION_ICONS } from "../../utils/sidebarNavIcons";
import { usePersistedPanelSize } from "../../hooks/usePersistedPanelSize";
import { useAccountProfilePortal } from "../../hooks/useAccountProfilePortal";
import useOperatorMarketBootstrap from "../../hooks/useOperatorMarketBootstrap";
import "../../operator-console.css";

const MANAGE_HOME = OPERATOR_MANAGE_HOME;
const OC_RAIL_DEFAULT = 248;
const OC_RAIL_MIN = 68;
const OC_RAIL_MAX = 380;
const OC_RAIL_COMPACT = 120;
const OC_MOBILE_MQ = "(max-width: 960px)";

const OperatorConsoleShell = ({
  children,
  activeSection = "pulse",
  onSectionChange,
  mainClassName = "",
}) => {
  const { houseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const userId = user?._id;
  const isConsoleRoot = Boolean(
    useMatch({ path: "/app/bethouse/manage/:houseId", end: true })
  );
  const isAddFixturesDesk = Boolean(
    useMatch({ path: "/app/bethouse/manage/:houseId/fixtures/add", end: true })
  );

  const {
    houses,
    housesStatus,
    dashboard,
    dashboardHouseId,
    dashboardStatus,
    dashboardsByHouse,
    dashboardStatusByHouse,
  } = useSelector((s) => s.operatorConsole);

  useOperatorMarketBootstrap(userId, houseId);

  const [houseMenuOpen, setHouseMenuOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const profileMenuRef = useRef(null);
  const avatarRef = useRef(null);

  const { size: railWidth, setClampedSize: setRailWidth, reset: resetRailWidth } =
    usePersistedPanelSize("bb-operator-rail-w", {
      defaultSize: OC_RAIL_DEFAULT,
      minSize: OC_RAIL_MIN,
      maxSize: OC_RAIL_MAX,
    });
  const isCompactRail = railWidth <= OC_RAIL_COMPACT;
  const pageStyle = useMemo(() => ({ "--oc-rail-w": `${railWidth}px` }), [railWidth]);

  const profileMenuStyle = useAccountProfilePortal(
    profileOpen,
    avatarRef,
    { mobileMq: OC_MOBILE_MQ },
    [railWidth]
  );

  const onRailResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = railWidth;
      document.body.classList.add("oc-is-resizing");

      const onMove = (ev) => {
        setRailWidth(startSize + (ev.clientX - startX));
      };
      const onUp = () => {
        document.body.classList.remove("oc-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [railWidth, setRailWidth]
  );

  const wrapRail = useCallback(
    (asideNode) => (
      <div className="oc-rail-resize-wrap" style={{ width: railWidth }}>
        {asideNode}
        <div
          className="oc-rail-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={railWidth}
          aria-valuemin={OC_RAIL_MIN}
          aria-valuemax={OC_RAIL_MAX}
          title="Drag to resize · double-click to reset"
          onPointerDown={onRailResizePointerDown}
          onDoubleClick={resetRailWidth}
        />
      </div>
    ),
    [onRailResizePointerDown, railWidth, resetRailWidth]
  );

  const railClass = (extra = "") =>
    ["oc-rail", extra, isCompactRail ? "oc-rail-compact" : ""].filter(Boolean).join(" ");

  const bet = useMemo(
    () =>
      houseId
        ? houses.find((h) => String(h._id) === String(houseId)) || null
        : null,
    [houses, houseId]
  );

  const displayHouse = bet || resolveOperatorHouse(houses, houseId);
  const partnershipReady = isBetHousePartnershipReadyForConsole(displayHouse);
  const addFixturesPath = operatorAddFixturesPath(houseId);

  const noHouse = housesStatus === "succeeded" && houses.length === 0;
  const statusKey = String(displayHouse?.status || (noHouse ? "draft" : "draft")).toLowerCase();
  const statusMeta = HOUSE_STATUS[statusKey] || HOUSE_STATUS.draft;
  const syncDashboard = shouldSyncDashboard(statusKey);
  const { dashboard: dashboardForDisplay } = useMemo(
    () =>
      operatorDashboardForHouse({
        dashboard,
        dashboardHouseId,
        dashboardsByHouse,
        houseId,
        dashboardStatus,
        dashboardStatusByHouse,
      }),
    [
      dashboard,
      dashboardHouseId,
      dashboardsByHouse,
      houseId,
      dashboardStatus,
      dashboardStatusByHouse,
    ]
  );
  const dashboardReady = Boolean(
    dashboardForDisplay &&
      (dashboardsByHouse[String(houseId)] ||
        String(dashboardHouseId) === String(houseId))
  );

  const loadHouses = useCallback(() => {
    if (!userId) return Promise.resolve();
    return dispatch(fetchOperatorHouses(userId));
  }, [dispatch, userId]);

  const loadDashboard = useCallback(() => {
    if (!houseId || !syncDashboard) return Promise.resolve();
    return dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId, syncDashboard]);

  useEffect(() => {
    if (housesStatus !== "succeeded" || !houses.length) return;
    const target = resolveOperatorHouse(houses, houseId);
    if (!target) return;
    if (!houseId || String(houseId) !== String(target._id)) {
      navigate(`${MANAGE_HOME}/${target._id}`, { replace: true });
    }
  }, [housesStatus, houses, houseId, navigate]);

  useEffect(() => {
    if (!houseId || !userId) return;
    dispatch(fetchHousePartners({ betId: houseId, operatorUserId: userId }));
  }, [dispatch, houseId, userId]);

  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      const inProfile = profileRef.current?.contains(e.target);
      const inMenu = profileMenuRef.current?.contains(e.target);
      if (!inProfile && !inMenu) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [profileOpen]);

  const handleSignOut = () => {
    setProfileOpen(false);
    dispatch(logoutAppUser());
    navigate("/app/bethouse");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadHouses();
      await loadDashboard();
      if (houseId && userId) {
        await dispatch(fetchHousePartners({ betId: houseId, operatorUserId: userId }));
      }
    } finally {
      setRefreshing(false);
    }
  };

  const sectionBadges = useMemo(() => {
    const openTickets = dashboardForDisplay?.activity?.openTickets ?? 0;
    return {
      treasury: dashboardReady && dashboardForDisplay?.treasury?.isLowBalance ? "!" : null,
      risk:
        dashboardReady && (dashboardForDisplay?.fixtures?.suspended ?? 0) > 0
          ? String(dashboardForDisplay.fixtures.suspended)
          : null,
      tickets: openTickets > 0 ? String(openTickets) : null,
    };
  }, [
    dashboardReady,
    dashboardForDisplay?.treasury?.isLowBalance,
    dashboardForDisplay?.fixtures?.suspended,
    dashboardForDisplay?.activity?.openTickets,
  ]);

  const railQuickStats = useMemo(() => {
    if (noHouse || !dashboardForDisplay) return null;
    return {
      openTickets: Number(dashboardForDisplay?.activity?.openTickets) || 0,
      available: Number(dashboardForDisplay?.treasury?.availableBalance) || 0,
    };
  }, [noHouse, dashboardForDisplay]);

  const handleSectionClick = (sectionId) => {
    if (noHouse) return;

    if (sectionId === "bettors" && isConsoleRoot && onSectionChange) {
      onSectionChange(sectionId);
      return;
    }

    const deskPath = operatorDeskPath(houseId, sectionId);
    if (deskPath) {
      navigate(deskPath);
      return;
    }

    if (isConsoleRoot && onSectionChange) {
      onSectionChange(sectionId);
      return;
    }

    navigate(`${MANAGE_HOME}/${houseId}`, { state: { section: sectionId } });
  };

  const isDeskLayout = mainClassName.includes("oc-desk-main");
  const mainClasses = ["oc-main", mainClassName].filter(Boolean).join(" ");
  const layoutClasses = ["oc-layout", isDeskLayout ? "oc-layout-desk" : ""]
    .filter(Boolean)
    .join(" ");

  const bootstrapping =
    (housesStatus === "loading" || housesStatus === "idle") && !houses.length;
  const housesFailed = housesStatus === "failed" && !houses.length;
  const railLocked = noHouse || bootstrapping;

  const dormantStatusMeta = bootstrapping
    ? { label: "Loading", tone: "primary" }
    : noHouse
      ? { label: "Setup", tone: "primary" }
      : statusMeta;

  return (
    <div className="oc-page" style={pageStyle}>
      <div className={`${layoutClasses}${railLocked ? " oc-layout-dormant" : ""}`}>
        {wrapRail(
        <aside
          className={railClass(railLocked ? "oc-rail-dormant" : "")}
          style={{ width: railWidth }}
        >
          <div className="oc-rail-top">
            <div className="oc-brand">
              <span className="oc-brand-icon" aria-hidden>
                <OPERATOR_BRAND_ICON size={15} strokeWidth={2.25} />
              </span>
              <div className="oc-brand-copy">
                <strong className="oc-brand-title">Operator console</strong>
                <span className="oc-brand-tag">House desk</span>
              </div>
            </div>
            <button
              type="button"
              className="oc-rail-collapse"
              onClick={() => setRailWidth(isCompactRail ? OC_RAIL_DEFAULT : OC_RAIL_MIN)}
              aria-label={isCompactRail ? "Expand sidebar" : "Collapse sidebar"}
              title={isCompactRail ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCompactRail ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>

          <div className={`oc-house-card${railLocked ? " oc-house-card-dormant" : ""}`}>
            <button
              type="button"
              className="oc-house-switcher"
              onClick={() => !railLocked && houses.length > 1 && setHouseMenuOpen((v) => !v)}
              disabled={railLocked || houses.length <= 1}
            >
              <span className="oc-house-mono">
                {bootstrapping ? (
                  <span className="oc-skel-bone oc-skel-mono" aria-hidden />
                ) : noHouse ? (
                  "—"
                ) : (
                  houseMonogram(displayHouse?.name)
                )}
                {!railLocked && statusKey === "active" && (
                  <span className="oc-live-dot" aria-hidden />
                )}
              </span>
              <div className="oc-house-copy">
                <strong>
                  {bootstrapping
                    ? "Betting house"
                    : noHouse
                      ? "No house yet"
                      : displayHouse?.name || "Betting house"}
                </strong>
              </div>
              <span className={`oc-status oc-status-${dormantStatusMeta.tone}`}>
                {dormantStatusMeta.label}
              </span>
              {!railLocked && houses.length > 1 && (
                <ChevronDown size={14} className="oc-house-chevron" aria-hidden />
              )}
            </button>

            {houseMenuOpen && houses.length > 1 && (
              <ul className="oc-house-menu">
                {houses.map((h) => (
                  <li key={h._id}>
                    <button
                      type="button"
                      className={String(h._id) === String(houseId) ? "oc-house-menu-active" : ""}
                      onClick={() => {
                        setHouseMenuOpen(false);
                        navigate(`${MANAGE_HOME}/${h._id}`);
                      }}
                    >
                      {h.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {bootstrapping ? (
              <div className="oc-house-meta">
                <span className="oc-house-meta-muted">Fetching your markets…</span>
              </div>
            ) : noHouse ? (
              <div className="oc-house-meta">
                <span className="oc-house-meta-muted">Create a house to activate this desk</span>
              </div>
            ) : (
              <>
                {railQuickStats ? (
                  <div className="oc-house-stats" aria-label="House snapshot">
                    <span className="tabular-nums">
                      {railQuickStats.openTickets} open ticket
                      {railQuickStats.openTickets === 1 ? "" : "s"}
                    </span>
                    <span className="oc-house-stats-dot" aria-hidden />
                    <span className="tabular-nums">
                      {formatCash(railQuickStats.available, { compact: true })} avail
                    </span>
                  </div>
                ) : (
                  <div className="oc-house-stats" aria-label="House snapshot">
                    <span>— open tickets</span>
                    <span className="oc-house-stats-dot" aria-hidden />
                    <span>— avail</span>
                  </div>
                )}
                {isPartneredHouse(displayHouse) ? (
                  <div className="oc-house-meta">
                    <span className="oc-partnered">
                      Partnered · {partnerCount(displayHouse)} members
                    </span>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className={`oc-nav-wrap${railLocked ? " oc-nav-wrap-dormant" : ""}`}>
            <nav className="oc-nav" aria-label="Console categories">
              {OPERATOR_NAV_CATEGORIES.map((category) => (
                <div key={category.id} className="oc-nav-category">
                  <p className="oc-nav-category-label">{category.label}</p>
                  <div
                    className="oc-nav-category-items"
                    role="group"
                    aria-label={category.label}
                  >
                    {category.sectionIds.map((sectionId) => {
                      const section = getOperatorSection(sectionId);
                      if (!section) return null;
                      const active =
                        activeSection === section.id &&
                        !(section.id === "fixtures" && isAddFixturesDesk);
                      return (
                        <React.Fragment key={section.id}>
                          <button
                            type="button"
                            className={`oc-nav-item ${active ? "oc-nav-item-active" : ""}${
                              railLocked ? " oc-nav-item-dormant" : ""
                            }`}
                            onClick={() => handleSectionClick(section.id)}
                            aria-current={active ? "page" : undefined}
                            disabled={railLocked}
                            title={
                              bootstrapping
                                ? "Loading markets…"
                                : noHouse
                                  ? "Create a house to unlock"
                                  : isCompactRail
                                    ? `${section.label} — ${section.hint}`
                                    : section.hint
                            }
                          >
                            <span className="oc-nav-indicator" aria-hidden />
                            <span className="oc-nav-icon">
                              <SidebarNavIcon
                                icon={OPERATOR_SECTION_ICONS[section.id]}
                                size={14}
                              />
                            </span>
                            <span className="oc-nav-copy">
                              <strong>{section.label}</strong>
                              <em>{section.hint}</em>
                            </span>
                            {sectionBadges[section.id] && (
                              <span className="oc-nav-badge">{sectionBadges[section.id]}</span>
                            )}
                          </button>
                          {section.id === "fixtures" && !railLocked && addFixturesPath ? (
                            <Link
                              to={addFixturesPath}
                              className={`oc-nav-sub-action${
                                isAddFixturesDesk ? " oc-nav-sub-action-active" : ""
                              }${partnershipReady ? "" : " oc-nav-sub-action-disabled"}`}
                              aria-disabled={!partnershipReady}
                              aria-current={isAddFixturesDesk ? "page" : undefined}
                              title={
                                partnershipReady
                                  ? "Add fixtures to this house"
                                  : "All partners must accept before adding fixtures"
                              }
                              onClick={(e) => {
                                if (!partnershipReady) e.preventDefault();
                              }}
                            >
                              <span className="oc-nav-sub-action-icon" aria-hidden>
                                <Plus size={12} />
                              </span>
                              <span className="oc-nav-sub-action-copy">
                                <strong>Add fixtures</strong>
                                <em>Browse catalog</em>
                              </span>
                            </Link>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          <div className="oc-rail-foot">
            <div className="oc-rail-foot-bar">
              <Link
                to="/app/bethouse"
                className="oc-icon-btn"
                aria-label="Home"
                title="Home"
              >
                <Home size={15} />
              </Link>
              {!railLocked && houseId ? (
                <Link
                  to={`/app/bethouse/${houseId}`}
                  className="oc-icon-btn"
                  aria-label="View public house"
                >
                  <ExternalLink size={15} />
                </Link>
              ) : (
                <span
                  className="oc-icon-btn oc-icon-btn-disabled"
                  aria-hidden
                  title={bootstrapping ? "Loading…" : "Create a house first"}
                >
                  <ExternalLink size={15} />
                </span>
              )}
              <button
                type="button"
                className="oc-icon-btn"
                onClick={handleRefresh}
                disabled={refreshing || bootstrapping}
                aria-label="Refresh"
              >
                <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
              </button>
              <div className="app-deck-profile-wrap oc-rail-profile" ref={profileRef}>
                <button
                  type="button"
                  ref={avatarRef}
                  onClick={() => setProfileOpen((v) => !v)}
                  className={`app-deck-avatar oc-rail-avatar ${
                    profileOpen ? "app-deck-avatar-open" : ""
                  }`}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  aria-label={
                    isAuthenticated
                      ? `Account, ${user?.name || ""}`
                      : "Account menu"
                  }
                >
                  {isAuthenticated ? getAccountInitial(user) : "?"}
                </button>
                {profileOpen &&
                  profileMenuStyle &&
                  createPortal(
                    <AccountProfileMenu
                      ref={profileMenuRef}
                      user={user}
                      isAuthenticated={isAuthenticated}
                      onSignOut={handleSignOut}
                      onClose={() => setProfileOpen(false)}
                    className="app-profile-menu-portal"
                    style={profileMenuStyle}
                    />,
                    document.body
                  )}
              </div>
            </div>
          </div>

          {displayHouse?.createdAt && (
            <p className="oc-founded-signature">
              Founded <time dateTime={displayHouse.createdAt}>{formatFounded(displayHouse.createdAt)}</time>
            </p>
          )}
        </aside>
        )}

        <main className={mainClasses}>
          {housesFailed ? (
            <div className="oc-loading oc-loading-inline">
              <p>Could not load your markets.</p>
              <button type="button" onClick={loadHouses}>
                Retry
              </button>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
};

export default OperatorConsoleShell;
